import React, { useRef } from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { useReactToPrint } from "react-to-print";
import { ViewExtrasSlot } from "../../../../common/components/projectSlots";

import { ColumnWidthInfo } from "../../../common/services/crudServices";
import { AuthorComposerViewStore } from "../../store/AuthorComposerViewStore";
import { useRootStore } from "../../../../store/contexts";
import { ShowMultiLine } from "../../../common/controls";
import { FieldAdornmentSlot } from "../../../../common/components/projectSlots";
import { Gender } from "../../../../api/graphql";
import { ShowString } from "../../../common/controls";
import { TaxIdType } from "../../../../api/graphql";
import { CrudRows } from "../../../common/types";
import { ShowFilesCompact } from "../../../common/controls";
import { IMenuAction } from "../../../../common/controls";
import { ShowDateTime } from "../../../common/controls";
import { CuitCertificateErrorType } from "../../../../api/graphql";
import { EmailVerificationIndicator } from "../../../common/controls/EmailVerificationIndicator";
import { ShowReference } from "../../../common/controls";
import { ShowDate } from "../../../common/controls";
import { FormControl } from "@mui/material";
import { ShowItemsCompact } from "../../../common/controls";
import { GridColumn } from "../../../common/types";

export type AuthorComposerViewFormProps = {
  onPrint?: (handlePrint: () => void) => void;
  onCancel?: () => void;
  onEdit?: () => void;
  showToolbox?: boolean;
  recordId?: string;
  firstNameValue?: { displayId: string; id: string } | null;
  firstName: string;
  lastNameValue?: { displayId: string; id: string } | null;
  lastName: string;
  genderOptions: { id: string; displayId: string }[];
  gender: Gender | "";
  taxIdTypeOptions: { id: string; displayId: string }[];
  taxIdType: TaxIdType | "";
  taxIdValue?: { displayId: string; id: string } | null;
  taxId: string;
  cuitCertificateFiles: CrudRows;
  cuitCertificateViewAction?: IMenuAction[];
  cuitCertificateIssuedAtValue?: { displayId: string; id: string } | null;
  cuitCertificateIssuedAt: Date | null;
  cuitCertificateErrorValue?: { displayId: string; id: string } | null;
  cuitCertificateError: string;
  cuitCertificateErrorTypeOptions: { id: string; displayId: string }[];
  cuitCertificateErrorType: CuitCertificateErrorType | "";
  dniDeliveredToSadaicAtValue?: { displayId: string; id: string } | null;
  dniDeliveredToSadaicAt: Date | null;
  dniValidatedAtValue?: { displayId: string; id: string } | null;
  dniValidatedAt: Date | null;
  emailValue?: { displayId: string; id: string } | null;
  email: string;
  phoneValue?: { displayId: string; id: string } | null;
  phone: string;
  nationalityValue: { displayId: string; id: string } | null;
  dateOfBirthValue?: { displayId: string; id: string } | null;
  dateOfBirth: string | null;
  streetAndNumberValue?: { displayId: string; id: string } | null;
  streetAndNumber: string;
  floorValue?: { displayId: string; id: string } | null;
  floor: string;
  suiteValue?: { displayId: string; id: string } | null;
  suite: string;
  postalCodeValue?: { displayId: string; id: string } | null;
  postalCode: string;
  countryValue: { displayId: string; id: string } | null;
  provinceValue: { displayId: string; id: string } | null;
  cityValue?: { displayId: string; id: string } | null;
  city: string;
  akaValue?: { displayId: string; id: string } | null;
  aka: string;
  ipiValue?: { displayId: string; id: string } | null;
  ipi: string;
  managementSocietiesRows: CrudRows;
  managementSocietiesVisibleColumns: GridColumn[];
  managementSocietiesSetColumnWiths: (x: ColumnWidthInfo[]) => void;
  publishersRows: CrudRows;
  publishersVisibleColumns: GridColumn[];
  publishersSetColumnWiths: (x: ColumnWidthInfo[]) => void;
  createdAt_Value?: { displayId: string; id: string } | null;
  createdAt_: Date | null;
  lastUpdatedAt_Value?: { displayId: string; id: string } | null;
  lastUpdatedAt_: Date | null;
  authorComposerViewStore: AuthorComposerViewStore;
};

export const AuthorComposerViewForm: React.FC<AuthorComposerViewFormProps> =
  observer(
    ({
      onPrint = () => {},
      onCancel = () => {},
      onEdit,
      showToolbox = true,
      recordId,
      firstName = "",
      lastName = "",
      genderOptions = [],
      gender = "" as Gender | "",
      taxIdTypeOptions = [],
      taxIdType = "" as TaxIdType | "",
      taxId = "",
      cuitCertificateFiles,
      cuitCertificateViewAction,
      cuitCertificateIssuedAt = null,
      cuitCertificateError = "",
      cuitCertificateErrorTypeOptions = [],
      cuitCertificateErrorType = "" as CuitCertificateErrorType | "",
      dniDeliveredToSadaicAt = null,
      dniValidatedAt = null,
      email = "",
      phone = "",
      nationalityValue,
      dateOfBirth = null,
      streetAndNumber = "",
      floor = "",
      suite = "",
      postalCode = "",
      countryValue,
      provinceValue,
      city = "",
      aka = "",
      ipi = "",
      managementSocietiesRows,
      managementSocietiesVisibleColumns,
      managementSocietiesSetColumnWiths,
      publishersRows,
      publishersVisibleColumns,
      publishersSetColumnWiths,
      createdAt_ = null,
      lastUpdatedAt_ = null,
      authorComposerViewStore,
    }) => {
      const rootStore = useRootStore();
      const { authStore, langStore } = rootStore;

      const componentRef = useRef(null);
      const handlePrint = useReactToPrint({
        content: () => componentRef.current,
      });

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              editForm={onEdit}
              printForm={() => onPrint(handlePrint)}
              closeForm={() => onCancel()}
            />
          )}
          <Box ref={componentRef}>
            <ViewExtrasSlot entity="AuthorComposer" recordId={recordId} />
            <GroupContainer
              id="personalInformation"
              title={langStore.groupAuthorComposerPersonalInformationLabel}
              collapsible={true}
              expanded={authorComposerViewStore.getCardState(
                "personalInformation"
              )}
              toggleExpand={() =>
                authorComposerViewStore.toggleCardState("personalInformation")
              }
            >
              <FormControl fullWidth>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="firstName"
                  mode="view"
                  recordId={recordId}
                  value={firstName}
                >
                  <ShowMultiLine
                    value={firstName}
                    label={langStore.AuthorComposerFirstNameLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="lastName"
                  mode="view"
                  recordId={recordId}
                  value={lastName}
                >
                  <ShowMultiLine
                    value={lastName}
                    label={langStore.AuthorComposerLastNameLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="gender"
                  mode="view"
                  recordId={recordId}
                  value={gender}
                >
                  <ShowString
                    value={
                      gender
                        ? genderOptions.find(
                            (o: { id: string }) => o.id === gender
                          )?.displayId || gender
                        : ""
                    }
                    label={langStore.AuthorComposerGenderLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="taxIdType"
                  mode="view"
                  recordId={recordId}
                  value={taxIdType}
                >
                  <ShowString
                    value={
                      taxIdType
                        ? taxIdTypeOptions.find(
                            (o: { id: string }) => o.id === taxIdType
                          )?.displayId || taxIdType
                        : ""
                    }
                    label={langStore.AuthorComposerTaxIdTypeLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="taxId"
                  mode="view"
                  recordId={recordId}
                  value={taxId}
                >
                  <ShowMultiLine
                    value={taxId}
                    label={langStore.AuthorComposerTaxIdLabel}
                  />
                </FieldAdornmentSlot>
                <ShowFilesCompact
                  rows={cuitCertificateFiles}
                  showLabel={true}
                  label={langStore.AuthorComposerCuitCertificateLabel}
                  menuActions={cuitCertificateViewAction}
                  columns={[
                    { name: "mimetype", title: langStore.fileTypeLabel },
                    { name: "name", title: langStore.fileNameLabel },
                    { name: "formattedSize", title: langStore.fileSizeLabel },
                  ]}
                />
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="cuitCertificateIssuedAt"
                  mode="view"
                  recordId={recordId}
                  value={cuitCertificateIssuedAt}
                >
                  <ShowDateTime
                    value={cuitCertificateIssuedAt}
                    label={langStore.AuthorComposerCuitCertificateIssuedAtLabel}
                  />
                </FieldAdornmentSlot>
                {["SysAdmin"].some((role) => authStore.hasRole(role)) && (
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="cuitCertificateError"
                    mode="view"
                    recordId={recordId}
                    value={cuitCertificateError}
                  >
                    <ShowMultiLine
                      value={cuitCertificateError}
                      label={langStore.AuthorComposerCuitCertificateErrorLabel}
                    />
                  </FieldAdornmentSlot>
                )}
                {["SysAdmin"].some((role) => authStore.hasRole(role)) && (
                  <FieldAdornmentSlot
                    entity="AuthorComposer"
                    field="cuitCertificateErrorType"
                    mode="view"
                    recordId={recordId}
                    value={cuitCertificateErrorType}
                  >
                    <ShowString
                      value={
                        cuitCertificateErrorType
                          ? cuitCertificateErrorTypeOptions.find(
                              (o: { id: string }) =>
                                o.id === cuitCertificateErrorType
                            )?.displayId || cuitCertificateErrorType
                          : ""
                      }
                      label={
                        langStore.AuthorComposerCuitCertificateErrorTypeLabel
                      }
                    />
                  </FieldAdornmentSlot>
                )}
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="dniDeliveredToSadaicAt"
                  mode="view"
                  recordId={recordId}
                  value={dniDeliveredToSadaicAt}
                >
                  <ShowDateTime
                    value={dniDeliveredToSadaicAt}
                    label={langStore.AuthorComposerDniDeliveredToSadaicAtLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="dniValidatedAt"
                  mode="view"
                  recordId={recordId}
                  value={dniValidatedAt}
                >
                  <ShowDateTime
                    value={dniValidatedAt}
                    label={langStore.AuthorComposerDniValidatedAtLabel}
                  />
                </FieldAdornmentSlot>

                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="email"
                  mode="view"
                  recordId={recordId}
                  value={email}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <ShowString
                      value={email}
                      label={langStore.AuthorComposerEmailLabel}
                    />
                    <EmailVerificationIndicator
                      email={email}
                      entityName="AuthorComposer"
                      recordId={recordId}
                      validationMode={true}
                    />
                  </Box>
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="phone"
                  mode="view"
                  recordId={recordId}
                  value={phone}
                >
                  <ShowMultiLine
                    value={phone}
                    label={langStore.AuthorComposerPhoneLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="nationality"
                  mode="view"
                  recordId={recordId}
                  value={nationalityValue}
                >
                  <ShowReference
                    label={langStore.AuthorComposerNationalityLabel}
                    selectedValue={langStore.displayIdByLang(
                      nationalityValue?.displayId
                    )}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="dateOfBirth"
                  mode="view"
                  recordId={recordId}
                  value={dateOfBirth}
                >
                  <ShowDate
                    value={dateOfBirth}
                    label={langStore.AuthorComposerDateOfBirthLabel}
                    locale={langStore.timeFormat}
                  />
                </FieldAdornmentSlot>
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="address"
              title={langStore.groupAuthorComposerAddressLabel}
              collapsible={true}
              expanded={authorComposerViewStore.getCardState("address")}
              toggleExpand={() =>
                authorComposerViewStore.toggleCardState("address")
              }
            >
              <FormControl fullWidth>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="streetAndNumber"
                  mode="view"
                  recordId={recordId}
                  value={streetAndNumber}
                >
                  <ShowMultiLine
                    value={streetAndNumber}
                    label={langStore.AuthorComposerStreetAndNumberLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="floor"
                  mode="view"
                  recordId={recordId}
                  value={floor}
                >
                  <ShowMultiLine
                    value={floor}
                    label={langStore.AuthorComposerFloorLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="suite"
                  mode="view"
                  recordId={recordId}
                  value={suite}
                >
                  <ShowMultiLine
                    value={suite}
                    label={langStore.AuthorComposerSuiteLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="postalCode"
                  mode="view"
                  recordId={recordId}
                  value={postalCode}
                >
                  <ShowMultiLine
                    value={postalCode}
                    label={langStore.AuthorComposerPostalCodeLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="country"
                  mode="view"
                  recordId={recordId}
                  value={countryValue}
                >
                  <ShowReference
                    label={langStore.AuthorComposerCountryLabel}
                    selectedValue={langStore.displayIdByLang(
                      countryValue?.displayId
                    )}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="province"
                  mode="view"
                  recordId={recordId}
                  value={provinceValue}
                >
                  <ShowReference
                    label={langStore.AuthorComposerProvinceLabel}
                    selectedValue={langStore.displayIdByLang(
                      provinceValue?.displayId
                    )}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="city"
                  mode="view"
                  recordId={recordId}
                  value={city}
                >
                  <ShowMultiLine
                    value={city}
                    label={langStore.AuthorComposerCityLabel}
                  />
                </FieldAdornmentSlot>
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="professionalInformation"
              title={langStore.groupAuthorComposerProfessionalInformationLabel}
              collapsible={true}
              expanded={authorComposerViewStore.getCardState(
                "professionalInformation"
              )}
              toggleExpand={() =>
                authorComposerViewStore.toggleCardState(
                  "professionalInformation"
                )
              }
            >
              <FormControl fullWidth>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="aka"
                  mode="view"
                  recordId={recordId}
                  value={aka}
                >
                  <ShowMultiLine
                    value={aka}
                    label={langStore.AuthorComposerAkaLabel}
                  />
                </FieldAdornmentSlot>
                <FieldAdornmentSlot
                  entity="AuthorComposer"
                  field="ipi"
                  mode="view"
                  recordId={recordId}
                  value={ipi}
                >
                  <ShowMultiLine
                    value={ipi}
                    label={langStore.AuthorComposerIpiLabel}
                  />
                </FieldAdornmentSlot>
                <ShowItemsCompact
                  rows={managementSocietiesRows}
                  showLabel={true}
                  label={langStore.AuthorComposerManagementSocietiesLabel}
                  columns={managementSocietiesVisibleColumns}
                  setColumnWidths={managementSocietiesSetColumnWiths}
                  entityName="AuthorCollectingSociety"
                  enableDownload={true}
                  onRowClick={(rowId) =>
                    rootStore.openSubentityModal(
                      "authorCollectingSociety",
                      rowId
                    )
                  }
                />
                <ShowItemsCompact
                  rows={publishersRows}
                  showLabel={true}
                  label={langStore.AuthorComposerPublishersLabel}
                  columns={publishersVisibleColumns}
                  setColumnWidths={publishersSetColumnWiths}
                  entityName="AuthorPublisher"
                  enableDownload={true}
                  onRowClick={(rowId) =>
                    rootStore.openSubentityModal("authorPublisher", rowId)
                  }
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="systemDates"
              title={langStore.groupAuthorComposerSystemDatesLabel}
              collapsible={true}
              expanded={authorComposerViewStore.getCardState("systemDates")}
              toggleExpand={() =>
                authorComposerViewStore.toggleCardState("systemDates")
              }
            >
              <FormControl fullWidth>
                <ShowDateTime
                  value={createdAt_}
                  label={langStore.AuthorComposerCreatedAt_Label}
                />
                <ShowDateTime
                  value={lastUpdatedAt_}
                  label={langStore.AuthorComposerLastUpdatedAt_Label}
                />
              </FormControl>
            </GroupContainer>
          </Box>
        </Grid>
      );
    }
  );
